function isAllowedTag(tagName) {
  const t = String(tagName || '').toUpperCase();
  return (
    t === 'H1' ||
    t === 'H2' ||
    t === 'H3' ||
    t === 'H4' ||
    t === 'P' ||
    t === 'UL' ||
    t === 'OL' ||
    t === 'LI' ||
    t === 'STRONG' ||
    t === 'EM' ||
    t === 'SPAN' ||
    t === 'A' ||
    t === 'BR'
  );
}

function isSafeUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return false;
  // allow hash links and relative paths
  if (raw.startsWith('#') || raw.startsWith('/')) return true;
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Very small client-side sanitizer to safely render AI-authored HTML.
 * - Removes disallowed tags (script/style/iframe/etc)
 * - Strips event handlers and inline styles
 * - Allows a small whitelist of tags + safe href on <a>
 */
export function sanitizeHtml(inputHtml) {
  const html = String(inputHtml || '').trim();
  if (!html) return '';
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    // In non-browser contexts, fall back to plain text-ish.
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc && doc.body;
  // Extremely defensive: some malformed inputs / odd runtimes can yield a document without a body.
  // Never hard-crash the app while rendering a post.
  if (!body) {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  // Some runtimes/browsers may not expose the global Node constant.
  // Use numeric nodeType fallbacks to avoid hard crashes.
  const COMMENT_NODE =
    (typeof window.Node !== 'undefined' && window.Node.COMMENT_NODE) ||
    (typeof Node !== 'undefined' && Node.COMMENT_NODE) ||
    8;
  const ELEMENT_NODE =
    (typeof window.Node !== 'undefined' && window.Node.ELEMENT_NODE) ||
    (typeof Node !== 'undefined' && Node.ELEMENT_NODE) ||
    1;

  const walk = (node) => {
    if (!node) return;

    // Remove comment nodes
    if (node.nodeType === COMMENT_NODE) {
      node.remove();
      return;
    }

    if (node.nodeType === ELEMENT_NODE) {
      const el = node;
      const tag = el.tagName;

      // Remove obviously dangerous tags
      const dangerous = ['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META'];
      if (dangerous.includes(tag)) {
        el.remove();
        return;
      }

      // If tag not allowed, unwrap it (keep children text)
      if (!isAllowedTag(tag)) {
        const parent = el.parentNode;
        if (parent) {
          while (el.firstChild) parent.insertBefore(el.firstChild, el);
          el.remove();
          // children moved; parent continues walking via recursion below
          return;
        }
      }

      // Strip dangerous attributes
      [...el.attributes].forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on')) {
          el.removeAttribute(attr.name);
          return;
        }
        if (name === 'style') {
          el.removeAttribute(attr.name);
          return;
        }
        if (tag !== 'A' && (name === 'href' || name === 'target' || name === 'rel')) {
          el.removeAttribute(attr.name);
          return;
        }
        // keep no other attributes on non-links
        if (tag !== 'A' && name !== 'class') {
          el.removeAttribute(attr.name);
        }
      });

      if (tag === 'A') {
        const href = el.getAttribute('href');
        if (!isSafeUrl(href)) {
          el.removeAttribute('href');
        }
        const target = el.getAttribute('target');
        if (target === '_blank') {
          el.setAttribute('rel', 'noopener noreferrer');
        } else {
          el.removeAttribute('target');
          el.removeAttribute('rel');
        }
      }
    }

    // Walk children (copy list because we might mutate)
    const children = [...(node.childNodes || [])];
    children.forEach(walk);
  };

  walk(body);
  return body.innerHTML || '';
}


